import React, { useState } from "react";
import { motion } from "framer-motion";
import moment from "moment";
import { ApiSubscriptionPlan, ApiPaymentMethods } from "src/api/types";
import Button from "src/components/Button";
import MandateMethods from "src/components/checkout/MandateMethods";
import usePost from "src/hooks/rest/usePost";
import useAuth from "src/hooks/selectors/useAuth";
import useAlert from "src/hooks/useAlert";
import ManualCheckoutSummary from "src/components/checkout/ManualCheckoutSummary";
import useWindowSize from "src/hooks/useWindowSize";
import { Trans, useTranslation } from "react-i18next";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import usePlans from "src/hooks/api/usePlans";

interface SubscriptionPlanSelectedProps {
	plan: ApiSubscriptionPlan;
	onCancel: () => void;
}

const SubscriptionPlanSelected = ({
	plan,
	onCancel,
}: SubscriptionPlanSelectedProps) => {
	const { tenant } = useTenant();
	const { interval } = usePlans();
	const { t } = useTranslation("subscription", {
		keyPrefix: "subscription-plan-selected",
	});
	const [subscribeToPlan] = usePost(
		`/auth/subscriptions/plans/${plan.id}/subscribe`
	);
	const [posting, setPosting] = useState(false);
	const [method, setMethod] = useState({
		type: "ideal",
		issuer: "",
	}) as any;
	const [sendAlert] = useAlert();
	const auth = useAuth();
	const size = useWindowSize();
	const promos = auth.company.promos;
	const canUseTrial = auth?.company?.statuses?.can_use_trial;
	const trialDays = 14;
	const products = [
		{
			description: `${tenant.name}, ${plan.name}`,
			price: interval === "MONTHLY" ? plan.price : plan.price_per_year,
		},
	];

	if (promos.includes("euro_subscription_deal")) {
		products.push({
			description: t("black-friday-sale"),
			price: -plan.price - 0.01,
		});
	}

	const getTrialDisclaimer = () => {
		if (canUseTrial && !promos.includes("euro_subscription_deal")) {
			return (
				<Trans i18nKey="trial-disclaimer-fee-later" {...{ t }}>
					Dit totale bedrag wordt pas op
					<b className="text-primary">
						{{
							date: moment()
								.add(trialDays, "days")
								.format("DD-MM-YYYY"),
						}}
					</b>
					afgeschreven
				</Trans>
			);
		}

		return t("trial-disclaimer-fee-direct");
	};

	const handleError = () => {
		sendAlert({
			type: "error",
			title: t("error.title"),
			text: t("error.text"),
		});
		setPosting(false);
	};

	const handleSubmit = (method: ApiPaymentMethods, issuer?: string) => {
		setPosting(true);
		subscribeToPlan({
			payment_method: method,
			redirect_url: `/subscription/${plan.slug}/thanks`,
			issuer,
			interval,
		})
			.then(({ data }) => {
				const url = data?.redirect_url;
				if (url) {
					window.location = url;
				} else {
					handleError();
				}
			})
			.catch(handleError);
	};

	return (
		<motion.div
			animate={{
				opacity: 1,
				x: 0,
				transition: {
					delay: 0.2,
					type: "timing",
				},
			}}
			initial={{
				opacity: 0,
				x: -200,
			}}
			className="rounded-md md:rounded-l-none md:mt-12 md:mb-4 flex flex-col gap-8 bg-white relative p-6 md:px-10 md:p-8"
		>
			{!posting ? (
				<>
					<div className="flex-1 flex flex-col gap-6">
						<div>
							<strong
								onClick={() => onCancel()}
								className="cursor-pointer "
							>
								<i className="fas fa-arrow-left mr-2"></i>
							</strong>
						</div>
						<div className="flex flex-col gap-2">
							<strong>{t("summary.title")}</strong>
							<ManualCheckoutSummary
								{...{ products }}
								summary={{
									vat: 0.21,
									partner_fee: 1,
								}}
							/>
						</div>
						<p>{getTrialDisclaimer()}</p>

						{!auth.company.statuses.has_active_mandate && (
							<MandateMethods
								idealOnly
								value={method}
								onClick={(type, issuer) =>
									setMethod({ type, issuer })
								}
							/>
						)}
					</div>

					{size.isPhone && <div className="h-12"></div>}

					<div
						className={
							size.isPhone
								? "fixed left-0 bottom-0 right-0 p-3 bg-dark-accent flex justify-center items-center"
								: ""
						}
					>
						{auth.company.statuses.has_active_mandate ? (
							<Button
								className="w-full flex-1"
								onClick={() => handleSubmit("ideal")}
							>
								<i className="fas fa-check mr-4"></i>
								{t("button.confirm")}
							</Button>
						) : (
							<Button
								className="w-full flex-1"
								disabled={!method.type || !method.issuer}
								onClick={() =>
									handleSubmit(method.type, method.issuer)
								}
							>
								{canUseTrial &&
								!promos.includes("euro_subscription_deal") ? (
									<>
										{!size.isPhone && (
											<i className="fas fa-check mr-4"></i>
										)}
										{t("button.start-trial")}
									</>
								) : (
									<>
										{!size.isPhone && (
											<i className="fas fa-check mr-4"></i>
										)}
										{t("button.confirm")}
									</>
								)}
							</Button>
						)}
					</div>
				</>
			) : (
				<div className="flex-1 flex justify-center items-center md:my-12">
					<span className="base-loader"></span>
				</div>
			)}
		</motion.div>
	);
};

export default SubscriptionPlanSelected;
